import { useCallback, useState } from "react";
import PimcoreService from "@/services/pimcoreService";
import { convertUtcToLocalDateTime, decodeHtmlEntities, formatSingleInputSelectionForDisplay, formatToMMDDYYYY, convertUTCStringToLocal, normalizeEscapedNewlinesIfString } from "@/utils/helper";
import { AuditTrailEntry } from "@/context/data.types";
import { handleApiError } from "@/hooks/useApiErrorHandler";
import { GenericAuditLogPayload } from "@/types";
import { useGlobalStateContext } from "@/context/GlobalContext";

type UseGenericAuditLogResult = {
  logs: AuditTrailEntry[];
  isLoadingAudit: boolean;
  fetchGenericAuditLog: (payload?: GenericAuditLogPayload) => Promise<AuditTrailEntry[]>;
  setLogs: React.Dispatch<React.SetStateAction<AuditTrailEntry[]>>;
};

const useGenericAuditLog = (
  options?: {
    onErrorMessage?: string;
  }
): UseGenericAuditLogResult => {
  const [logs, setLogs] = useState<AuditTrailEntry[]>([]);
  const [isLoadingAudit, setIsLoadingAudit] = useState<boolean>(false);
  const { setUserLogs } = useGlobalStateContext();
  // Helper function to format "added on" field to MM/DD/YYYY (same as form)
  const formatAddedOnDate = useCallback((val: string): string => {
    return formatToMMDDYYYY(convertUTCStringToLocal(val));
  }, []);

  // Normalize valuesUpdated to convert dateTime fields to local date time
  const normalizeValuesUpdated = useCallback((valuesUpdated: any): Record<string, any> => {
    if (!valuesUpdated || typeof valuesUpdated !== "object") return {};

    const result: Record<string, any> = {};
    Object.entries(valuesUpdated).forEach(([key, value]) => {
      const v: any = value ?? {};
      const fieldType = v?.type ?? null;
      const fieldName = v?.fieldName ?? null;
      const fieldLabel = v?.fieldLabel ?? null;
      
      // Check if this is the "added on" field
      const isAddedOnField = 
        key?.toLowerCase() === "addedon" || 
        fieldName?.toLowerCase() === "addedon" ||
        fieldLabel?.toLowerCase() === "added on";

      const convertIfDateTime = (val: unknown) => {
        if (fieldType === "dateTime" && typeof val === "string" && val) {
          // For "added on" field, use the same formatting as the form (MM/DD/YYYY without time)
          if (isAddedOnField) {
            return formatAddedOnDate(val);
          }
          return convertUtcToLocalDateTime(val);
        }
        return val ?? null;
      };

      const convertValue = (val: unknown) => {
        const dateConverted = convertIfDateTime(val);
        const formatted = formatSingleInputSelectionForDisplay(dateConverted);
        return normalizeEscapedNewlinesIfString(formatted);
      };

      result[key] = {
        fieldLabel: v?.fieldLabel ?? null,
        fieldName: v?.fieldName ?? null,
        oldValue: convertValue(v?.oldValue),
        newValue: convertValue(v?.newValue),
        type: fieldType,
      };
    });

    return result;
  }, [formatAddedOnDate]);

  const fetchGenericAuditLog = useCallback(
    async (payload?: GenericAuditLogPayload): Promise<AuditTrailEntry[]> => {
      if (!payload || !payload.pageId || !payload.objectIds || payload.objectIds.length === 0) {
        return [];
      }
      setUserLogs([]);
      setIsLoadingAudit(true);
      try {
        const resp = await PimcoreService.getGenericAuditTrail(payload);
        const rawLogs: unknown = (resp && (resp as any).data && Array.isArray((resp as any).data))
          ? (resp as any).data
          : Array.isArray(resp)
            ? resp
            : [];

        const updatedLogs: AuditTrailEntry[] = (Array.isArray(rawLogs) ? rawLogs : []).flatMap((log: any) => {
          const decodedLog = decodeHtmlEntities(log);
          // Normalize date first
          const withLocalDate = {
            ...decodedLog,
            dateTime: convertUtcToLocalDateTime(decodedLog?.dateTime),
          };

          // Omit any `sections` property from the base object to keep types clean
          const { sections, ...baseEntry } = withLocalDate;

          // If new API shape with sections is present, flatten into multiple entries
          if (Array.isArray(sections) && sections.length > 0) {
            return sections.map((section: any) => {
              const sectionValuesUpdated = normalizeValuesUpdated(section?.valuesUpdated ?? {});
              const sectionName = section?.name ?? baseEntry?.section;
              return {
                ...baseEntry,
                valuesUpdated: sectionValuesUpdated,
                section: sectionName,
              } as AuditTrailEntry;
            });
          }
          // Backward compatibility: original shape already has valuesUpdated at root
          return [{
            ...baseEntry,
            valuesUpdated: normalizeValuesUpdated(decodedLog?.valuesUpdated ?? {}),
          } as AuditTrailEntry];
        });

        setLogs(updatedLogs);
        return updatedLogs;
      } catch (error) {
        handleApiError(error, options?.onErrorMessage || "Failed to fetch change log.");
        return [];
      } finally {
        setIsLoadingAudit(false);
      }
    },
    [options?.onErrorMessage, normalizeValuesUpdated]
  );

  return { logs, isLoadingAudit, fetchGenericAuditLog, setLogs };
};

export default useGenericAuditLog;


